import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Custom hook that ensures user is authenticated before performing actions
 * If not authenticated, redirects to login page with return URL
 *
 * @returns {Function} A function that checks auth and executes callback if authenticated
 *
 * @example
 * const requireAuth = useRequireAuth();
 *
 * const handleSave = () => {
 *   requireAuth(() => {
 *     saveDeal(dealId);
 *   });
 * };
 */
export const useRequireAuth = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requireAuth = useCallback(
    (callback) => {
      if (!isAuthenticated) {
        // Store the current location to redirect back after login
        const returnUrl = location.pathname + location.search;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
        return false;
      }
      // User is authenticated, execute the callback
      if (typeof callback === "function") {
        callback();
      }
      return true;
    },
    [isAuthenticated, navigate, location]
  );

  return requireAuth;
};
